import type { CSSProperties } from "react";

/** Preset mark heights — wordmark and gap scale from these values. */
export type AniLogoSize = "xs" | "sm" | "md" | "lg";

/**
 * Lockup ratios — all scale linearly from mark size.
 * Wordmark SVG viewBox includes vertical padding; height ≈ mark keeps visible
 * letterforms at ~68% of mark (between legacy 18/32 too-small and 1.24× too-large).
 * @see design-system/docs/branding-logo.md
 */
export const ANI_LOGO_WORDMARK_HEIGHT_RATIO = 0.88;
export const ANI_LOGO_GAP_RATIO = 0.22;

/** Per-size wordmark height — sm/xs slightly smaller for compact chrome headers. */
export const ANI_LOGO_WORDMARK_HEIGHT_RATIOS: Record<AniLogoSize, number> = {
  xs: 0.84,
  sm: 0.84,
  md: ANI_LOGO_WORDMARK_HEIGHT_RATIO,
  lg: ANI_LOGO_WORDMARK_HEIGHT_RATIO,
};

export function getAniLogoWordmarkHeightRatio(size: AniLogoSize = "lg"): number {
  return ANI_LOGO_WORDMARK_HEIGHT_RATIOS[size];
}

/** logo.svg circle cy / viewBox height (16.46). */
export const ANI_LOGO_MARK_INK_CENTER_Y_RATIO = 7.64 / 16.46;
/** logo_text.svg visible ink center / viewBox height (66.51); y ≈ 12.71–66.51. */
export const ANI_LOGO_WORDMARK_INK_CENTER_Y_RATIO = 39.61 / 66.51;

/** Align wordmark ink center to mark ink center when flex centers each img bbox. */
export function getAniLogoWordmarkTranslateYRatio(
  wordmarkHeightRatio: number = ANI_LOGO_WORDMARK_HEIGHT_RATIO,
): number {
  const markBoxOffset = ANI_LOGO_MARK_INK_CENTER_Y_RATIO - 0.5;
  const wordmarkBoxOffset =
    (ANI_LOGO_WORDMARK_INK_CENTER_Y_RATIO - 0.5) * wordmarkHeightRatio;
  return markBoxOffset - wordmarkBoxOffset;
}

/** Mark edge length per preset size. */
export const ANI_LOGO_MARK_SIZES: Record<AniLogoSize, string> = {
  xs: "1.5rem",
  sm: "1.75rem",
  md: "2rem",
  lg: "3rem",
};

export type AniLogoLockupStyles = {
  container: CSSProperties;
  mark: CSSProperties;
  wordmark: CSSProperties;
};

export function getAniLogoLockupStyles(size: AniLogoSize = "lg"): AniLogoLockupStyles {
  const mark = ANI_LOGO_MARK_SIZES[size];
  const wordmarkHeightRatio = getAniLogoWordmarkHeightRatio(size);
  const wordmarkTranslateYRatio = getAniLogoWordmarkTranslateYRatio(wordmarkHeightRatio);
  return {
    container: {
      height: mark,
      gap: `calc(${mark} * ${ANI_LOGO_GAP_RATIO})`,
    },
    mark: {
      height: mark,
      width: mark,
    },
    wordmark: {
      height: `calc(${mark} * ${wordmarkHeightRatio})`,
      transform: `translateY(calc(${mark} * ${wordmarkTranslateYRatio}))`,
    },
  };
}
